import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../componentes/modal/modal";
import Button from "../../../../componentes/button/button";
import Input from "../../../../componentes/input/input";
import InputMoney from "../../../../componentes/input/inputMoney";
import SolicitacaoCompraService from "../../../../services/resource/solicitacaoCompraService";
import { formatarMoeda, formatarData } from "../../../../utils/solicitacoes/formatters";
import { humanizarEtapa } from "../../../../utils/solicitacoes/statusConfig";

const service = new SolicitacaoCompraService();

/**
 * Busca e seleção de uma Solicitação de Compras COMPLEMENTAR para o recebimento (ver seção
 * "Solicitações Complementares" do spec) — backend já exclui a própria solicitação, solicitações
 * encerradas e as que já foram usadas como complemento em outro recebimento
 * (GET /buscar-complemento). Só falta escolher o valor a utilizar antes de confirmar.
 */
export default function SelecionarComplementoModal({ isOpen, onClose, solicitacaoPrincipalId, idsJaAdicionados = [], onSelecionar }) {
    const [query, setQuery] = useState("");
    const [resultados, setResultados] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [candidataEscolhida, setCandidataEscolhida] = useState(null);
    const [valorUtilizado, setValorUtilizado] = useState("");

    useEffect(() => {
        if (!isOpen) return undefined;

        setCarregando(true);
        let cancelado = false;
        const timeout = setTimeout(() => {
            service.buscarComplemento(solicitacaoPrincipalId, query)
                .then(res => { if (!cancelado) setResultados(res.data.filter(c => !idsJaAdicionados.includes(c.id))); })
                .catch(() => {
                    if (!cancelado) {
                        setResultados([]);
                        toast.error("Erro ao buscar solicitações.");
                    }
                })
                .finally(() => { if (!cancelado) setCarregando(false); });
        }, 350);

        return () => { cancelado = true; clearTimeout(timeout); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, query, solicitacaoPrincipalId]);

    function fechar() {
        setQuery("");
        setResultados([]);
        setCandidataEscolhida(null);
        setValorUtilizado("");
        onClose();
    }

    function escolher(candidata) {
        setCandidataEscolhida(candidata);
        setValorUtilizado(candidata.valor ?? "");
    }

    function confirmar() {
        if (!valorUtilizado || Number(valorUtilizado) <= 0) {
            toast.error("Informe o valor a utilizar desta solicitação complementar.");
            return;
        }
        onSelecionar({
            solicitacaoComplementarId: candidataEscolhida.id,
            numero: candidataEscolhida.numero,
            solicitanteNome: candidataEscolhida.solicitanteNome,
            valorUtilizado: Number(valorUtilizado),
            valorCompra: candidataEscolhida.valorCompra,
        });
        fechar();
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={fechar}
            title="Adicionar Solicitação Complementar"
            subtitle="Busque por número, solicitante ou fornecedor — só aparecem solicitações de compras que já chegaram na Confirmação de Recebimento (ou passaram dela) e não usadas como complemento em outro recebimento."
            className="w-[90%] min-h-[60vh]"
            footer={
                candidataEscolhida ? (
                    <div className="flex justify-end gap-3">
                        <Button variant="outline" label="Voltar" onClick={() => setCandidataEscolhida(null)} />
                        <Button variant="blue" label="Adicionar" iconLeft="fa fa-plus" onClick={confirmar} />
                    </div>
                ) : (
                    <div className="flex justify-end">
                        <Button variant="outline" label="Fechar" onClick={fechar} />
                    </div>
                )
            }
        >
            <div className="p-3 py-4 flex flex-col gap-4">
                {!candidataEscolhida && (
                    <>
                        <Input
                            type="text"
                            label="Buscar"
                            placeholder="Número, solicitante ou fornecedor..."
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                        />

                        {carregando && (
                            <div className="text-sm text-muted-foreground text-center py-6">Buscando solicitações...</div>
                        )}

                        {!carregando && resultados.length === 0 && (
                            <div className="text-sm text-muted-foreground text-center py-6">
                                Nenhuma solicitação encontrada.
                            </div>
                        )}

                        {!carregando && resultados.length > 0 && (
                            <div className="overflow-x-auto border border-border rounded-lg max-h-[50vh] overflow-y-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-muted sticky top-0">
                                        <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                            <th className="px-3 py-2 font-semibold">Nº Solicitação</th>
                                            <th className="px-3 py-2 font-semibold">Solicitante</th>
                                            <th className="px-3 py-2 font-semibold">Fornecedor</th>
                                            <th className="px-3 py-2 font-semibold">Etapa</th>
                                            <th className="px-3 py-2 font-semibold">Data</th>
                                            <th className="px-3 py-2 font-semibold text-right">Valor</th>
                                            <th className="px-3 py-2 font-semibold text-right">Ação</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {resultados.map(candidata => (
                                            <tr key={candidata.id} className="border-t border-border">
                                                <td className="px-3 py-2 text-foreground whitespace-nowrap">{candidata.numero}</td>
                                                <td className="px-3 py-2 text-foreground whitespace-nowrap">{candidata.solicitanteNome}</td>
                                                <td className="px-3 py-2 text-foreground whitespace-nowrap">{candidata.fornecedorNome || "—"}</td>
                                                <td className="px-3 py-2 text-foreground whitespace-nowrap">{humanizarEtapa(candidata.etapaAtual)}</td>
                                                <td className="px-3 py-2 text-foreground whitespace-nowrap">{formatarData(candidata.dataCriacao)}</td>
                                                <td className="px-3 py-2 text-right text-foreground whitespace-nowrap">{formatarMoeda(candidata.valor)}</td>
                                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                                    <Button variant="blue" label="Selecionar" onClick={() => escolher(candidata)} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {candidataEscolhida && (
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-2 gap-3 text-sm rounded-lg bg-muted px-4 py-3">
                            <div>
                                <span className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Solicitação</span>
                                <span className="block text-foreground font-medium">{candidataEscolhida.numero}</span>
                            </div>
                            <div>
                                <span className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Solicitante</span>
                                <span className="block text-foreground font-medium">{candidataEscolhida.solicitanteNome}</span>
                            </div>
                        </div>

                        <InputMoney
                            label="Valor a utilizar desta solicitação"
                            required
                            locale="pt-BR"
                            value={valorUtilizado}
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            mode="decimal"
                            onFocus={(e) => e.target.select()}
                            onChange={e => setValorUtilizado(e.target.value)}
                            helpText="Pré-preenchido com o valor da própria solicitação — ajuste conforme o valor realmente utilizado na nota."
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
}
